// D4 — Caller drill-down page (/workspace/:apiId/callers/:callerId)
//
// 服务商问"这个 user 在我的 API 上调了什么"：
//   - 顶部 breadcrumb + 类型 pill (🔑 API Key / 👤 OAuth user)
//   - KPI: calls / success / spent / firstSeen / lastSeen
//   - Top endpoints 该 caller 调最多的
//   - Status code 分布
//   - 国家分布
//   - 该 caller 的最近日志（apiLogs 加 callerId filter）
//
// 全数据 scoped 到 (apiServiceId, callerId)，绝不 leak 其他 API 上的行为。

import React, { useState, useCallback, useMemo } from 'react';
import { XShell } from '../shell.jsx';
import { exactAnalyticsBounds } from './workspace-sections/analytics-window.js';
import { useDetailResource, useDetailLogs, historicalDetailError } from './workspace-sections/detail-resources.js';
import { XApi } from '../services.js';
import { WorkspaceShell } from '../ui/workspace-shell.jsx';
import {
  KpiCard,
  KpiGrid,
  RangePicker,
  Section,
  PieSlices,
  EmptyTable,
  EmptyPie,
  LoadingHint,
  StatusCodePill,
  MethodPill,
  CountryCell,
  DataTable,
  TableFooter,
  ExportCsvButton,
  fmtNum,
  fmtPct,
  fmtUsd,
  fmtTinyUsd,
  fmtRelDate,
} from './workspace-sections/analytics-shared.jsx';


export const WorkspaceCallerPage = () => {
  const { authed, user } = XShell.useAuth();
  const { route } = XShell.useRoute();
  const owner = user?._raw?.id ?? user?.id ?? null;
  return authed ? <CallerState key={JSON.stringify([owner, route.params.apiId, route.params.callerId])} /> : <div>请登录后查看调用者详情</div>;
};
const CallerState = () => {
  const { route, navigate } = XShell.useRoute();
  const apiId = route.params.apiId;
  const callerId = route.params.callerId;

  const [range, setRange] = useState('30d');
  const [customRange, setCustomRange] = useState({ from: '', to: '' });
  const bounds = useMemo(() => exactAnalyticsBounds(range, customRange), [range, customRange.from, customRange.to]);
  const windowKey = JSON.stringify([apiId, callerId, bounds]);
  const loadApi = useCallback(() => XApi.apiSvc.get(apiId), [apiId]);
  const metadata = useDetailResource(apiId, loadApi);
  const api = metadata.data;
  const loadDetail = useCallback(() => XApi.providerDashboardService.callerDetail(apiId, callerId, bounds), [apiId, callerId, bounds]);
  const resource = useDetailResource(windowKey, bounds ? loadDetail : null);
  const detail = resource.data ?? { callerType:'api_key',callerHandle:'—',totalCalls:0,totalSpent:0,topEndpoints:[],statusCodes:{},countries:[] };
  const filter = useMemo(() => ({ callerId }), [callerId]);
  const { logs, logCursor, logHasMore, logsLoading, logsError, fetchLogs } = useDetailLogs(apiId, bounds, filter);
  const onSelect = (key, sub) => {
    navigate(
      'workspace-api',
      sub ? { apiId, section: key, subsection: sub } : { apiId, section: key },
    );
  };

  if (!api) return <div>{metadata.error ? <><span role="alert">API 信息加载失败</span><button onClick={metadata.retry}>重试</button></> : 'Loading caller…'}</div>;
  const pieData = [
    { label: '2xx', value: detail.statusCodes['2xx'] || 0, color: '#22a06b' },
    { label: '3xx', value: detail.statusCodes['3xx'] || 0, color: '#5b8def' },
    { label: '4xx', value: detail.statusCodes['4xx'] || 0, color: '#e08e0b' },
    { label: '5xx', value: detail.statusCodes['5xx'] || 0, color: '#c0392b' },
  ];

  return (
    <WorkspaceShell
      api={api}
      activeSection="analytics"
      activeSubsection="users"
      onSelect={onSelect}
    >
      <div style={{ padding: '24px 32px 64px' }}>
        {/* breadcrumb */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginBottom: 16,
            fontSize: 11,
            color: 'var(--fg-3)',
          }}
        >
          <button
            type="button"
            onClick={() => navigate('workspace-api', { apiId, section: 'analytics', subsection: 'users' })}
            style={{
              padding: '2px 8px',
              background: 'transparent',
              border: '1px solid var(--bd-1)',
              borderRadius: 4,
              color: 'var(--fg-2)',
              cursor: 'pointer',
              fontSize: 11,
            }}
          >← Users</button>
          <span>/</span>
          <CallerTypePill type={detail.callerType} />
          <span className="mono" style={{ color: 'var(--fg-0)' }} title={detail.callerHandle}>
            {detail.callerHandle}
          </span>
        </div>

        {/* header + range picker */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 16,
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          <div style={{ minWidth: 0 }}>
            <h2 style={{ margin: 0, fontSize: 16, fontWeight: 500, color: 'var(--fg-0)', letterSpacing: '-0.01em' }}>
              Caller detail
            </h2>
            <div style={{ fontSize: 11, color: 'var(--fg-3)', marginTop: 4 }}>
              该 user 在 {api.name} 上的 REST 调用 · {detail.callerType === 'oauth_user' ? 'OAuth 用户授权' : 'API key 调用'}
            </div>
          </div>
          <RangePicker
            value={range}
            onChange={setRange}
            customRange={customRange}
            onCustomRangeChange={setCustomRange}
          />
        </div>

        {!bounds && <div role="alert">日期范围必须完整、有效、正序且不超过 365 天（UTC）</div>}
        {resource.error && <div role="alert">{historicalDetailError(resource.error) ?? '调用者数据加载失败'} <button onClick={resource.retry}>重试数据</button></div>}
        {resource.loading && <LoadingHint />}
        {resource.data && <>
        {/* KPI */}
        <KpiGrid>
          <KpiCard
            label="调用次数"
            value={fmtNum(detail.totalCalls)}
            dim={detail.totalCalls === 0}
          />
          <KpiCard
            label="成功率"
            value={detail.totalCalls > 0 ? fmtPct(detail.successRate / 100) : '—'}
            dim={detail.totalCalls === 0}
          />
          <KpiCard
            label="窗口内消费"
            value={fmtTinyUsd(detail.totalSpent)}
            dim={detail.totalSpent === 0}
          />
          <KpiCard
            label="首次见到"
            value={detail.firstSeen ? fmtRelDate(detail.firstSeen) : '—'}
            dim={!detail.firstSeen}
            hint={detail.firstSeen ? new Date(detail.firstSeen).toLocaleDateString() : ''}
          />
          <KpiCard
            label="最近一次"
            value={detail.lastSeen ? fmtRelDate(detail.lastSeen) : '—'}
            dim={!detail.lastSeen}
            hint={detail.lastSeen ? new Date(detail.lastSeen).toLocaleString() : ''}
          />
        </KpiGrid>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 16, marginBottom: 16 }}>
          <Section title={`Top endpoints (${detail.topEndpoints.length})`}>
            {detail.topEndpoints.length > 0 ? (
              <DataTable>
                <thead>
                  <tr style={{ color: 'var(--fg-3)', textAlign: 'left', fontWeight: 400 }}>
                    <th style={{ padding: '6px 8px' }}>Endpoint</th>
                    <th style={{ padding: '6px 8px', textAlign: 'right' }}>调用</th>
                    <th style={{ padding: '6px 8px', textAlign: 'right' }}>调用方消费</th>
                    <th style={{ padding: '6px 8px', textAlign: 'right' }}>占比</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.topEndpoints.map((ep) => {
                    const pct = detail.totalCalls > 0
                      ? (ep.calls / detail.totalCalls) * 100
                      : 0;
                    return (
                      <tr
                        data-endpoint-id={ep.endpointId}
                        key={ep.endpointId}
                        style={{ borderTop: '1px solid var(--bd-1)', cursor: 'pointer' }}
                        onClick={() => navigate('workspace-endpoint', { apiId, endpointId: ep.endpointId })}
                        title="点击进入该 endpoint 的完整分析"
                        onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg-1)')}
                        onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                      >
                        <td style={{ padding: '6px 8px' }}>
                          <div style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                            <MethodPill method={ep.method} />
                            <span style={{ color: 'var(--fg-0)' }}>{ep.name}</span>
                          </div>
                          <div style={{ fontSize: 10, color: 'var(--fg-3)', marginTop: 2 }} className="mono">
                            {ep.path}
                          </div>
                        </td>
                        <td style={{ padding: '6px 8px', textAlign: 'right' }} className="mono">{fmtNum(ep.calls)}</td>
                        <td style={{ padding: '6px 8px', textAlign: 'right' }} className="mono">{fmtTinyUsd(ep.cost)}</td>
                        <td style={{ padding: '6px 8px', textAlign: 'right' }} className="mono">{pct.toFixed(1)}%</td>
                      </tr>
                    );
                  })}
                </tbody>
              </DataTable>
            ) : (
              <EmptyTable cols={4} rows={3} hint="该 caller 在窗口内没有调用任何 endpoint" />
            )}
          </Section>

          <Section title="状态码分布">
            {pieData.some((d) => d.value > 0) ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <PieSlices data={pieData} size={120} />
                <div style={{ fontSize: 11, flex: 1 }}>
                  {pieData.map((d) => (
                    <div key={d.label} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                      <span style={{
                        display: 'inline-block', width: 12, height: 12, background: d.color,
                        borderRadius: 3, boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.15)',
                      }} />
                      <span style={{ color: 'var(--fg-1)' }}>{d.label}</span>
                      <span className="mono" style={{ marginLeft: 'auto', color: 'var(--fg-2)' }}>{fmtNum(d.value)}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <EmptyPie size={120} />
                <div style={{ fontSize: 11, color: 'var(--fg-3)', flex: 1 }}>无可用状态码分布</div>
              </div>
            )}
          </Section>
        </div>

        {/* 国家分布 */}
        {detail.countries.length > 0 && (
          <Section title={`国家分布 (${detail.countries.length})`}>
            <DataTable>
              <thead>
                <tr style={{ color: 'var(--fg-3)', textAlign: 'left', fontWeight: 400 }}>
                  <th style={{ padding: '6px 8px' }}>国家</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right' }}>调用</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right' }}>占比</th>
                </tr>
              </thead>
              <tbody>
                {detail.countries.map((c) => (
                  <tr key={c.country} style={{ borderTop: '1px solid var(--bd-1)' }}>
                    <td style={{ padding: '6px 8px' }}><CountryCell iso={c.country} /></td>
                    <td style={{ padding: '6px 8px', textAlign: 'right' }} className="mono">{fmtNum(c.calls)}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right' }} className="mono">
                      {detail.totalCalls > 0
                        ? `${((c.calls / detail.totalCalls) * 100).toFixed(1)}%`
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
          </Section>
        )}

        </>}
        {logsError && <div role="alert">日志加载失败 <button onClick={() => fetchLogs({ append: logs.length > 0, cursor: logCursor })}>重试日志</button></div>}
        {/* 该 caller 的调用日志 */}
        <Section
          title="调用日志"
          action={
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 10, color: 'var(--fg-3)' }}>自动 scoped 到本 caller</span>
              <ExportCsvButton
                rows={logs}
                filename={`caller-${callerId.slice(0, 8)}-logs-${new Date().toISOString().slice(0, 10)}.csv`}
                columns={[
                  { key: 'createdAt', label: 'Time', transform: (v) => new Date(v).toISOString() },
                  { key: 'method', label: 'Method' },
                  { key: 'statusCode', label: 'Status' },
                  { key: 'responseTime', label: 'Latency ms' },
                  { key: 'cost', label: 'Cost USD' },
                  { key: 'country', label: 'Country' },
                  { key: 'id', label: 'Log ID' },
                ]}
              />
            </div>
          }
        >
          {logsLoading && logs.length === 0 ? (
            <LoadingHint />
          ) : logs.length === 0 ? (
            <EmptyTable cols={6} rows={4} hint="该 caller 在窗口内没有调用记录" />
          ) : (
            <>
              <DataTable>
                <thead>
                  <tr style={{ color: 'var(--fg-3)', textAlign: 'left', fontWeight: 400 }}>
                    <th style={{ padding: '6px 8px' }}>时间</th>
                    <th style={{ padding: '6px 8px' }}>方法</th>
                    <th style={{ padding: '6px 8px' }}>状态</th>
                    <th style={{ padding: '6px 8px' }}>端点</th>
                    <th style={{ padding: '6px 8px', textAlign: 'right' }}>延迟</th>
                    <th style={{ padding: '6px 8px', textAlign: 'right' }}>花费</th>
                    <th style={{ padding: '6px 8px' }}>地区</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => (
                    <tr key={log.id} data-log-id={log.id} style={{ borderTop: '1px solid var(--bd-1)' }}>
                      <td style={{ padding: '6px 8px', color: 'var(--fg-2)', whiteSpace: 'nowrap' }} className="mono">
                        {new Date(log.createdAt).toLocaleString()}
                      </td>
                      <td style={{ padding: '6px 8px' }}><MethodPill method={log.method} /></td>
                      <td style={{ padding: '6px 8px' }}><StatusCodePill code={log.statusCode} /></td>
                      <td style={{ padding: '6px 8px', maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <span className="mono" style={{ color: 'var(--fg-3)' }} title={log.requestUrl}>
                          {log.requestUrl?.split('?')[0]?.slice(-32) || '—'}
                        </span>
                      </td>
                      <td style={{ padding: '6px 8px', textAlign: 'right' }} className="mono">{log.responseTime}ms</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right' }} className="mono">{fmtTinyUsd(log.cost)}</td>
                      <td style={{ padding: '6px 8px' }}><CountryCell iso={log.country} /></td>
                    </tr>
                  ))}
                </tbody>
              </DataTable>
              <TableFooter
                hasMore={logHasMore}
                loading={logsLoading}
                totalLoaded={logs.length}
                onLoadMore={() => fetchLogs({ append: true, cursor: logCursor })}
              />
            </>
          )}
        </Section>
      </div>
    </WorkspaceShell>
  );
};

// 复用：caller type pill。导出方便其他页面（Users 榜单 / log table）共用
export const CallerTypePill = ({ type }) => {
  const isOauth = type === 'oauth_user';
  return (
    <span
      title={isOauth ? 'OAuth user-context（客户用账号授权后调用）' : '普通 API key 调用'}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 3,
        padding: '1px 6px',
        background: isOauth ? '#5b8def22' : '#22a06b22',
        color: isOauth ? '#5b8def' : '#22a06b',
        borderRadius: 999,
        fontSize: 10,
        fontWeight: 500,
      }}
    >
      {isOauth ? '👤 OAuth' : '🔑 API Key'}
    </span>
  );
};
